/**
 * Visual transfer-count rail used inside {@link MiniModalSelect} options.
 */

import React, { useMemo } from "react";
import { getUIColorPalette } from "../../theme/ColorPalette";

interface TransferCountDecorationProps {

    /** Maximum allowed transfers; `null` renders an unlimited marker. */
    "maxTransfers": number | null;

    /** Enables dark palette variants for dot borders. */
    "isDarkUi": boolean;
}

/**
 * Geometric infinity mark; avoids font-metric drift of the Unicode `∞` glyph.
 *
 * @param props Icon color.
 * @returns Inline SVG sized for the select leading slot.
 */
const UnlimitedTransfersIcon: React.FC<{ "color": string }> = ( { color } ) => {
    return (
        <svg
            aria-hidden="true"
            className="block h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Symmetric lemniscate centered in the 24×24 viewBox (Lucide-style). */}
            <path
                d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z"
                stroke={color}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
            />
        </svg>
    );
};

/**
 * Renders a compact dot rail (or infinity marker) for transfer preference rows.
 *
 * @param props Transfer cap and theme flag.
 * @returns Inline decoration node.
 */
export const TransferCountDecoration: React.FC<TransferCountDecorationProps> = ( {
    maxTransfers,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    if ( maxTransfers === null ) {
        return (
            <span
                aria-hidden="true"
                className="inline-flex h-full w-full items-center justify-center"
            >
                <UnlimitedTransfersIcon color={palette.brandPrimary} />
            </span>
        );
    }

    if ( maxTransfers === 0 ) {
        return (
            <span
                aria-hidden="true"
                className="inline-flex h-full w-full items-center justify-center"
            >
                <span
                    className="h-1.5 w-7 rounded-full"
                    style={{ "backgroundColor": palette.brandPrimary }}
                />
            </span>
        );
    }

    return (
        <span
            aria-hidden="true"
            className="inline-flex h-full w-full items-center justify-center gap-1"
        >
            {Array.from( { "length": maxTransfers }, ( _, index ) => (
                <span
                    className="h-2 w-2 rounded-full"
                    key={index}
                    style={{ "backgroundColor": palette.brandPrimary }}
                />
            ) )}
        </span>
    );
};
